import React, { useContext, useCallback } from 'react';
import { SortOrder } from '../../enums';
import { HeadCellType } from '../../interfaces/common';
import { TableContext } from '../../contexts/TableContext';

interface HeadCellProps {
  cell: HeadCellType;
}

const HeadCell: React.FC<HeadCellProps> = ({ cell }) => {
  const { order, orderBy, setOrder, setOrderBy, stickyHeader } = useContext(TableContext);

  const isSorted = cell.sortEnabled && orderBy === cell.id;

  const isActions = cell.id === 'actions' || (typeof cell.label === 'string' && cell.label.toLowerCase().includes('action'));
  const effectiveAlign = cell.textAlign || (isActions ? 'right' : undefined);

  const handleHeadCellClick = useCallback(() => {
    let nextSortOrder: SortOrder = SortOrder.asc;
    if (orderBy === cell.id) {
      if (order === SortOrder.asc) nextSortOrder = SortOrder.desc;
      else nextSortOrder = SortOrder.asc;
    }

    setOrder(nextSortOrder);
    setOrderBy(cell.id);
  }, [setOrder, setOrderBy, cell.id, order, orderBy]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (cell.sortEnabled && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        handleHeadCellClick();
      }
    },
    [cell.sortEnabled, handleHeadCellClick]
  );

  return (
    <th
      className={`${cell.sortEnabled ? 'sorting' : 'sorting_disabled'} ${isSorted ? (order === SortOrder.asc ? 'sorting_asc' : 'sorting_desc') : ''} ${effectiveAlign === 'right' ? 'text-end' : ''}`}
      tabIndex={cell.sortEnabled ? 0 : undefined}
      role={cell.sortEnabled ? 'button' : undefined}
      aria-sort={isSorted ? (order === SortOrder.asc ? 'ascending' : 'descending') : cell.sortEnabled ? 'none' : undefined}
      rowSpan={1}
      colSpan={1}
      style={{
        width: cell.width,
        textAlign: effectiveAlign,
        cursor: cell.sortEnabled ? 'pointer' : 'default',
        userSelect: cell.sortEnabled ? 'none' : 'auto',
        ...(stickyHeader ? { position: 'sticky', top: 0, zIndex: 2, background: 'var(--table-band, var(--surf))' } : {}),
      }}
      onClick={cell.sortEnabled ? handleHeadCellClick : undefined}
      onKeyDown={handleKeyDown}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          justifyContent: effectiveAlign === 'right' ? 'flex-end' : effectiveAlign === 'center' ? 'center' : 'flex-start',
          width: '100%',
        }}
      >
        <span>{cell.label}</span>
        {cell.sortEnabled && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: '13px',
              opacity: isSorted ? 1 : 0.4,
              transition: 'opacity 0.15s ease',
            }}
          >
            {isSorted ? (
              order === SortOrder.asc ? (
                <i className="ri-arrow-up-s-line" aria-hidden="true" style={{ fontSize: '13px', fontWeight: 600 }} />
              ) : (
                <i className="ri-arrow-down-s-line" aria-hidden="true" style={{ fontSize: '13px', fontWeight: 600 }} />
              )
            ) : (
              <i className="ri-arrow-up-down-line" aria-hidden="true" style={{ fontSize: '13px' }} />
            )}
          </span>
        )}
      </span>
    </th>
  );
};

export default HeadCell;
